import { useNavState } from '@/providers/NavStateProvider';
import { useEffect } from 'react';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { Link, useNavigate } from 'react-router-dom';
import { useUpdateWorkspaceDetail } from '@/hooks/api/0141_update_workspace_detail';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { useLoading } from '@/providers/LoadingProvider';
import { toastSuccess } from '@/lib/toast';
import { useWorkspaceInvalidate } from '@/hooks/api/0101_workspace';
import { MenuButtonGroup } from '@/components/features/SettingMenuButtonGroup';
import { FormGroup } from '@/components/element/Form/FormGroup';
import { ConsumptionTaxRuleLogic } from '@/hooks/api/models/logics';

type FormInputs = {
  identifier: string;
  invoice_manual_url: string;
  consumption_tax_rule: string;
};

export const EditWorkspaceDetail = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle('スペース詳細設定編集'), []);
  useEffect(() => setBreadcrumbs([{ label: `スペース設定`, path: `/w/${_w_!.identifier}/setting` }]), []);

  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_ } = useCurrentWorkspace();
  const { mutate, data, isPending, error } = useUpdateWorkspaceDetail();
  const { invalidate } = useWorkspaceInvalidate(_w_!.identifier);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      invoice_manual_url: _w_!.invoice_manual_url,
      consumption_tax_rule: _w_!.consumption_tax_rule,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (data) {
      toastSuccess('変更が完了しました');
      invalidate(); // Workspaceのキャッシュクリア
      navigate(`/w/${_w_!.identifier}/setting`);
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  return (
    <WorkspaceLayout>
      <div className="container-fluid py-4">
        <MenuButtonGroup selected={'basic'} />

        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">スペース詳細設定編集</h5>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0">
                  <ApiErrorMessages error={error} />

                  <div className="form-group">
                    <label>請求書確認マニュアルリンク</label>
                    <input
                      type="text"
                      maxLength={250}
                      className={`form-control ${errors.invoice_manual_url && 'is-invalid'}`}
                      placeholder={'URL'}
                      {...register('invoice_manual_url', {
                        pattern: {
                          value: /^https?:\/\/[\w/:%#\$&\?\(\)~\.=\+\-]+$/,
                          message: 'URLを入力して下さい。',
                        },
                      })}
                    />
                    <div className="text-xs text-muted mt-1">請求書を処理する際に参照するマニュアルへのリンク</div>
                    {errors.invoice_manual_url && (
                      <div className="text-xs text-danger mt-1">{errors.invoice_manual_url.message}</div>
                    )}
                  </div>

                  <div className="form-group col-12 col-md-6">
                    <label>消費税計算ルール</label>
                    <select className="form-control" required={true} {...register('consumption_tax_rule')}>
                      {['floor', 'round', 'ceil'].map((tax_rule, i) => (
                        <option key={`tax-rule-${tax_rule}`} value={tax_rule}>
                          {ConsumptionTaxRuleLogic.nameToLabel(tax_rule)}
                        </option>
                      ))}
                    </select>
                    <div className="text-xs text-muted mt-1">消費税計算での端数の計算方法</div>
                  </div>
                </div>

                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link to={`/w/${_w_!.identifier}/setting`} className="btn btn-outline-secondary me-2">
                    戻る
                  </Link>
                  <button className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>変更を確定する
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
